import { ContactRoleProducer as BaseContactRole } from '@eigtech/contacts-types'
import { getContactRoleLabel } from '@eigtech/ui-shared-contacts'
import { useWatch } from '@eigtech/ui-shared-forms/shadcn'
import { Stack } from '@eigtech/ui/components/stack'
import {
  ContactForm,
  ContactFormArrayField,
  ContactFormSelectField,
  useContactFormArrayFieldRowContext,
  useContactFormContext,
} from './ContactFormSchema/ContactFormSchema'
import {
  ContactFormContactRole,
  ContactFormContactRoleEnumSchema,
} from './ContactFormSchema/RoleFieldSchema'
import { ContactInfoRowContainer } from './ContactInfoRowContainer'

export function RoleFieldArray(props: { requiredRole: BaseContactRole | undefined }) {
  return (
    <ContactFormArrayField
      RowContainer={(rowProps) => <RowContainer {...rowProps} {...props} />}
      defaultValue={{ role: '' as ContactForm['roles'][number]['role'] }}
      label="Roles"
      min={1}
      name="roles"
      renderRow={() => <RoleField {...props} />}
    />
  )
}

function RowContainer({
  requiredRole,
  ...props
}: { children: React.ReactNode; index: number } & RoleFieldProps) {
  const { isDisabled } = useRoleField({ requiredRole })

  return <ContactInfoRowContainer {...props} disabled={isDisabled} />
}

type RoleFieldProps = { requiredRole: BaseContactRole | undefined }
function RoleField({ requiredRole }: RoleFieldProps) {
  const { index } = useContactFormArrayFieldRowContext()

  const { role, isRequiredRole, isDefaultRole, isDisabled } = useRoleField({ requiredRole })

  const options = (
    isRequiredRole && requiredRole
      ? [{ value: requiredRole }]
      : isDefaultRole
        ? [{ value: role }]
        : ContactFormContactRoleEnumSchema.options.map((role) => ({
            value: role,
          }))
  ).map((option) => ({ label: getContactRoleLabel(option.value), ...option }))

  return (
    <Stack className="w-full gap-2">
      <ContactFormSelectField
        disabled={isDisabled}
        label="Role"
        name={`roles.${index}.role`}
        options={options}
        placeholder={options.length === 1 ? false : undefined}
      />
    </Stack>
  )
}

function useRoleField({ requiredRole }: RoleFieldProps) {
  const { control } = useContactFormContext()
  const { index } = useContactFormArrayFieldRowContext()

  // const isFirst = index === 0

  const role = useWatch({ control, name: `roles.${index}.role` })
  const isRequiredRole = !!requiredRole && role === requiredRole
  const isDefaultRole =
    !!role && !ContactFormContactRoleEnumSchema.options.includes(role as ContactFormContactRole)

  const isDisabled = isRequiredRole || isDefaultRole

  return { role, isRequiredRole, isDefaultRole, isDisabled }
}
